/**
 * GeneratingPlayer — the "your video is rendering" experience shown after the
 * user hits Generate. Shaped like a video player (so the real video can slot
 * straight in when it's done), with a softly-blurred backdrop, cycling
 * buzzwords, and a progress bar.
 *
 *  - Premium (real render): the bar climbs LINEARLY from 0% over an estimate of
 *    16× the final video length, then HOLDS at 99% until the webhook lands the
 *    real video (the "100%"). The clock is anchored to the order's `approved_at`
 *    (when the render actually started) so reopening the project mid-render
 *    RESUMES the bar instead of restarting at 0. The backend writes a naive-UTC
 *    timestamp (no tz), so we append 'Z' before parsing — otherwise the browser
 *    reads it as local time and elapsed jumps hours ahead. Falls back to mount
 *    time if approved_at is missing/unparseable.
 *  - Free (fast): no real render happens, so it completes in a few seconds and
 *    calls onDone() to hand off to the blurred preview + paywall.
 *
 * Backdrop: a blurred preview image when we have one (the plan's cover), else
 * an animated Vivideo-purple gradient.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Check } from 'lucide-react';
import { cn } from '@/v2/utils';
import i18n from '@/i18n';

// Momentum checklist (endowed progress): finished artifacts accumulate during the
// wait, so leaving mid-render feels like abandoning work already done. Each item
// reveals at a progress threshold, so it paces correctly in BOTH the fast free
// run (~10s) and a longer premium render. The free (fast) run drops "Video
// rendered" — no video is actually rendered for free users, so it would be a lie.
const CHECK_KEYS_FULL = ['checkScript', 'checkScenes', 'checkRender', 'checkThumb', 'checkCaption'] as const;
const CHECK_KEYS_FAST = ['checkScript', 'checkScenes', 'checkThumb', 'checkCaption'] as const;

const BUZZWORDS = [
  i18n.t('generatingPlayer.buzzWarmingUp', { ns: 'v2' }),
  i18n.t('generatingPlayer.buzzDirecting', { ns: 'v2' }),
  i18n.t('generatingPlayer.buzzAvatar', { ns: 'v2' }),
  i18n.t('generatingPlayer.buzzVoiceover', { ns: 'v2' }),
  i18n.t('generatingPlayer.buzzMotion', { ns: 'v2' }),
  i18n.t('generatingPlayer.buzzColorGrading', { ns: 'v2' }),
  i18n.t('generatingPlayer.buzzPerfecting', { ns: 'v2' }),
  i18n.t('generatingPlayer.buzzFinishing', { ns: 'v2' }),
];

export function GeneratingPlayer({
  durationSec, approvedAt, blurImageUrl, fast, onDone, progressPct,
}: {
  /** Plan length — drives the ~18× pace estimate (premium). */
  durationSec: number;
  /** When the render started (order.approved_at). Lets the bar RESUME after the
   *  project is reopened mid-render instead of restarting at 0. */
  approvedAt?: string;
  /** Blurred backdrop (e.g. the plan cover); falls back to a purple gradient. */
  blurImageUrl?: string;
  /** Free users: complete quickly and call onDone. */
  fast?: boolean;
  onDone?: () => void;
  /** A provider that REPORTS progress (LongVid) drives the bar with it instead of
   *  the time estimate; ignored when absent or not a number. */
  progressPct?: number | string | null;
}) {
  const { t } = useTranslation('v2');
  const [, force] = useState(0);
  const mounted = useRef(Date.now());
  const doneFired = useRef(false);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 200);
    return () => clearInterval(t);
  }, []);

  // Anchor the clock to when the render actually started (approved_at), so
  // reopening the project mid-render resumes the bar. Backend writes naive UTC →
  // append 'Z' so the browser doesn't read it as local time. Fast/free or a
  // missing/bad timestamp falls back to this mount's time.
  const startedAt = (() => {
    if (fast || !approvedAt) return mounted.current;
    const s = /[zZ]|[+-]\d\d:?\d\d$/.test(approvedAt) ? approvedAt : `${approvedAt}Z`;
    const t = Date.parse(s);
    return Number.isNaN(t) ? mounted.current : t;
  })();
  const elapsed = Math.max(0, (Date.now() - startedAt) / 1000);

  // Free: a 10s run to 100% (no real render — long enough to feel like work,
  // short enough not to lose the impatient). Premium: a STEADY, linear climb
  // from 0% over the estimated render time — ≈ 12× the final video length —
  // holding at 99% until the real video lands (the parent swaps this out for the
  // finished video, which is the "100%").
  const estTotal = fast ? 10 : Math.max(40, durationSec * 12);
  const estimated = fast
    ? Math.min(100, (elapsed / estTotal) * 100)
    : Math.min(99, (elapsed / estTotal) * 100);
  // A reported percentage (LongVid) beats the estimate; still capped at 99 so the
  // finished video — not the bar — is the "100%".
  const reported = progressPct == null ? NaN : Number(progressPct);
  const pct = !fast && Number.isFinite(reported) ? Math.min(99, Math.max(0, reported)) : estimated;

  useEffect(() => {
    if (fast && pct >= 100 && !doneFired.current) {
      doneFired.current = true;
      onDone?.();
    }
  }, [fast, pct, onDone]);

  const word = BUZZWORDS[Math.floor(elapsed / 3.5) % BUZZWORDS.length];
  const overdue = !fast && elapsed > estTotal;
  // Free run has no real render → hide "Video rendered". Thresholds are evenly
  // spread (~10%→88%) across whatever number of items this mode shows.
  const checkKeys = fast ? CHECK_KEYS_FAST : CHECK_KEYS_FULL;
  const checkAtPct = checkKeys.map((_, i) => Math.round(10 + (78 * (i + 1)) / checkKeys.length));
  const checksShown = checkAtPct.filter((th) => pct >= th).length;
  const allDone = checksShown >= checkKeys.length;

  return (
    <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black border border-glass-border animate-fade-in">
      {/* Blurred backdrop */}
      {blurImageUrl ? (
        <img src={blurImageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'blur(16px)', transform: 'scale(1.15)' }} />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary/55 via-accent/40 to-primary/30 animate-pulse" style={{ filter: 'blur(8px)', transform: 'scale(1.1)' }} />
      )}
      {/* Lighter scrim when we have a real cover, so it stays visible; darker
          for the bare gradient so the spinner/text read well. */}
      <div className={cn('absolute inset-0', blurImageUrl ? 'bg-black/30' : 'bg-black/45')} />

      {/* Center: spinner + cycling buzzword + accumulating momentum checklist.
          Everything scales down on mobile so it fits the short aspect-video box. */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 sm:gap-2.5 text-center px-4 sm:px-6">
        <span className="w-8 h-8 sm:w-11 sm:h-11 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
          <Loader2 className="w-4 h-4 sm:w-6 sm:h-6 text-white animate-spin" />
        </span>
        <p className="text-xs sm:text-lg font-bold text-white drop-shadow-md transition-opacity duration-300">{word}</p>
        {/* Momentum checklist is FREE-only (fast): it's a conversion nudge for the
            fake preview. Premium (real render) just shows the spinner + buzzword. */}
        {fast && (
        <ul className={cn(
          'space-y-0.5 sm:space-y-1 text-left rounded-xl p-1 sm:p-1.5 transition-all duration-500',
          // All done → the list glows for the beat before it hands off to the
          // "your video is ready" reveal. The excitement peak.
          allDone && 'animate-pulse-glow',
        )}>
          {checkKeys.map((k, i) => {
            const done = i < checksShown;
            return (
              <li
                key={k}
                className={cn(
                  'flex items-center gap-1.5 sm:gap-2 rounded-lg px-1.5 py-0.5 text-[10px] sm:text-sm font-medium transition-all duration-300 drop-shadow',
                  done ? 'text-white animate-flash-done' : 'text-white/45',
                )}
              >
                {done ? (
                  <span className="flex w-3 h-3 sm:w-4 sm:h-4 shrink-0 items-center justify-center rounded-full bg-green-400/90 animate-pop">
                    <Check className="w-2 h-2 sm:w-3 sm:h-3 text-white" strokeWidth={3} />
                  </span>
                ) : (
                  <span className="w-3 h-3 sm:w-4 sm:h-4 rounded-full border border-white/40 shrink-0" />
                )}
                <span className={cn('transition-transform', done && 'animate-check-row')}>{t(`previewFunnel.${k}`)}</span>
              </li>
            );
          })}
        </ul>
        )}
        {overdue && <p className="text-[10px] sm:text-xs text-white/70 max-w-xs">{t('generatingPlayer.overdue')}</p>}
      </div>

      {/* % badge — big on desktop, trimmed on mobile so it doesn't dominate */}
      <span className="absolute top-2 right-2 sm:top-3 sm:right-3 px-2.5 py-1 sm:px-4 sm:py-2 rounded-xl sm:rounded-2xl bg-black/55 backdrop-blur-sm text-base sm:text-3xl font-extrabold text-white tabular-nums shadow-lg">
        {Math.round(pct)}%
      </span>

      {/* Loading bar — thick, glowing, with a travelling sheen */}
      <div className="absolute bottom-0 inset-x-0 h-2 sm:h-3 bg-black/40">
        <div
          className={cn(
            'relative h-full bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-shimmer shadow-[0_0_16px_rgba(139,92,246,0.7)]',
            fast ? 'transition-[width] duration-200' : 'transition-[width] duration-500 ease-out'
          )}
          style={{ width: `${pct}%` }}
        >
          {/* moving highlight sheen riding the leading edge */}
          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent bg-[length:200%_100%] animate-shimmer" />
          <span className="absolute right-0 top-0 h-full w-1.5 bg-white/80 blur-[2px]" />
        </div>
      </div>
    </div>
  );
}
